import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSnackbarStore } from '@/stores/snackbar'
import { useLoaderStore } from '@/stores/loader'
import { http, HTTPError } from '@/helpers/http'
import { useRouter } from 'vue-router'
import { track } from '@/helpers/mixpanelDes'
import { useRemoteImportStore } from '@/stores/remote-import'
import { useUserStore } from '@/stores/user'
import { useCreatorStore } from '@/stores/creator'
import ChunkUploaderClient from '@/helpers/chunkedUpload'
import { isValidImportUrl } from '@/helpers/url'
import { resolveImportUrlInfo } from '@/helpers/importUrlInfo'
import { audioVideoImportTrackingProperties } from '@/helpers/audioVideoImportTracking'
import { ImportTypes } from '@/helpers/importTypes'
import {
  ERemoteImportURLStatus,
  ERemoteImportURLType,
  TRemoteImportURL,
  type TUploadedFileInfo,
} from '@/types/remote-imports'
import type { TImportUrlInfo } from '@/types/remote-imports'
import type { ChunkedUploadModelRecordData } from '@/helpers/chunkedUpload'

export const useLocalImportStore = defineStore('local-import', () => {
  const router = useRouter()
  const importType = ref<ERemoteImportURLType | null>(null)
  const urls = ref<TRemoteImportURL[]>([])
  const dndFile = ref<File | null>(null)
  const user = useUserStore()
  const snack = useSnackbarStore()
  const loader = useLoaderStore()
  const defaultUploadLimits = {
    docx_import: 0,
    audio_import: 0,
    video_import: 0,
    pdf_to_flipbook: 0,
    pdf_import: 0,
    pptx_import: 0,
    fallback: 0,
  }
  const uploadResume = ref<ChunkedUploadModelRecordData | null>(null)
  let uploadStartedAt = new Date()

  const remoteImport = useRemoteImportStore()
  const acceptedFormats = ref<string>(
    'audio/mp4,.wav,audio/aac,audio/x-aac,audio/wav,video/mp4,video/x-ms-asf,video/quicktime,audio/wav,audio/x-wav,audio/mpeg,audio/mp3,audio/x-hx-aac-adts,audio/vnd.dlna.adts,audio/x-m4a,.m4a',
  )

  const importTypeToRoute: Partial<Record<ERemoteImportURLType, string>> = {
    [ERemoteImportURLType.Audio]: '/creator/ebook/from-podcast',
    [ERemoteImportURLType.Video]: '/creator/ebook/from-video',
    [ERemoteImportURLType.YouTube]: '/creator/ebook/youtube',
    [ERemoteImportURLType.Web]: '/creator/ebook/from-blog',
    [ERemoteImportURLType.DocX]: '/creator/ebook/from-docx',
    [ERemoteImportURLType.GDoc]: '/creator/ebook/from-gdoc',
    [ERemoteImportURLType.GSlides]: '/creator/presentation/from-gslides',
    [ERemoteImportURLType.Pdf]: '/creator/ebook/from-pdf',
    [ERemoteImportURLType.Pptx]: '/creator/ebook/from-pptx',
  }

  function trackAudioVideoUpload(failed: boolean, info?: TUploadedFileInfo, reason?: string) {
    const type = importType.value
    const file = urls.value[0]?.file ?? dndFile.value
    const helper = type ? ImportTypes.helpers[type] : undefined
    if (!type || !helper || !file || ![ERemoteImportURLType.Audio, ERemoteImportURLType.Video].includes(type)) {
      return
    }
    track(
      failed ? helper.trackErrorMsg : helper.trackSuccessTag,
      audioVideoImportTrackingProperties({
        importType: type,
        file,
        info,
        startedAt: uploadStartedAt,
        reason,
        dragAndDrop: !!dndFile.value,
      }),
    )
  }

  function isValidUrl(url: string): boolean {
    return isValidImportUrl(url)
  }

  async function navigateToUrlImport(input: string | TImportUrlInfo, silent = false): Promise<boolean> {
    const info = typeof input === 'string' ? await resolveImportUrlInfo(input, undefined, loader) : input
    if (!info) {
      if (!silent) {
        snack.add('Incorrect URL')
      }
      return false
    }
    const targetRoute = importTypeToRoute[info.type]
    if (!targetRoute) {
      snack.add('Incorrect URL')
      return false
    }
    remoteImport.dndUrl = info.url
    remoteImport.dndUrlType = info.type
    router.push(targetRoute)

    return true
  }

  const chunkedClient = new ChunkUploaderClient({
    errorCallback: (error: string) => {
      snack.add(error, { type: 'danger', permanent: true })
      trackAudioVideoUpload(true, undefined, error)
      if (dndFile.value) {
        track('project-creating-dnd-failed', {
          name: dndFile.value.name,
          size:
            dndFile.value.size < 1024 * 1024
              ? `${Math.round(dndFile.value.size / 1024)} KiB`
              : `${Math.round(dndFile.value.size / 1024 / 1024)} MiB`,
          type: dndFile.value.type,
        })
      }
      urls.value = []
    },
    progressCallback: (percent: number) => {
      if (!urls.value || !urls.value[0]) return
      urls.value[0].status = ERemoteImportURLStatus.Uploading
      if (percent > 100) {
        urls.value[0].progress = 100
      } else {
        urls.value[0].progress = percent
      }
    },
    successCallback: async (data: { fileLink: string; fileName: string; uploadedFileInfo?: TUploadedFileInfo }) => {
      if (urls.value[0] == null) return

      urls.value[0].status = ERemoteImportURLStatus.Uploaded
      urls.value[0].fileLink = data.fileLink
      if (['audio', 'video'].includes(importType.value ?? '')) {
        urls.value[0].info = data.uploadedFileInfo
        urls.value[0].status = ERemoteImportURLStatus.ReadyToTranscribe
        trackAudioVideoUpload(false, data.uploadedFileInfo)
        return
      }
      if (dndFile.value) {
        track('project-creating-dnd', {
          name: dndFile.value.name,
          size:
            dndFile.value.size < 1024 * 1024
              ? `${Math.round(dndFile.value.size / 1024)} KiB`
              : `${Math.round(dndFile.value.size / 1024 / 1024)} MiB`,
          type: dndFile.value.type,
        })
      }
      try {
        await remoteImport.importFile(true, data.fileName, urls.value[0])
      } catch (e) {
        const message = e instanceof HTTPError ? 'Error importing file' : (e instanceof Error ? e.message : 'Error importing file')
        snack.add(message, { type: 'danger', permanent: true })
      }
    },
    uploadLimits: user.publicConfig?.upload_limits || defaultUploadLimits,
    importModulesMap: user.publicConfig?.import_module_map || {},
  })

  async function resume(resumeData: ChunkedUploadModelRecordData, file: File) {
    const buffer = await chunkedClient.getChunkFromBlob(file, resumeData.last_complete_chunk).arrayBuffer()
    if (file.size !== resumeData.size || chunkedClient.calcHash(buffer) !== resumeData.last_complete_chunk_checksum) {
      urls.value.pop()
      snack.add('Upload resume error: wrong file selected')
      return
    }
    chunkedClient.resumeFrom(resumeData, file)
    uploadResume.value = null
  }

  async function addFiles(files: File[] | null) {
    const creator = useCreatorStore()
    if (!importType.value || !creator.importType) {
      detectUpload(files)
      return
    }
    if (importType.value && ['gdoc', 'gslides', 'youtube'].includes(importType.value)) {
      snack.add('File upload not allowed for this import type ' + importType.value)
      return
    }
    if (files?.[0]) {
      files.forEach((f) => importType.value && urls.value.push(new TRemoteImportURL(importType.value, '', f)))
    } else {
      throw new Error(`Import type not initialized`)
    }

    if (!acceptedFormats.value.split(',').includes(files[0].type)) {
      urls.value.pop()
      snack.add('Wrong file type selected')
      return
    }

    uploadStartedAt = new Date()
    try {
      if (uploadResume.value) {
        await resume(uploadResume.value, files[0])
      } else {
        chunkedClient.startUpload(files[0], importType.value)
      }
    } catch (e) {
      snack.add('Could not start the upload', { type: 'danger', permanent: true })
      urls.value = []
      throw e
    }
  }

  async function init(type: ERemoteImportURLType, accept?: string) {
    if (accept) {
      acceptedFormats.value = accept
    }
    await user.getPublicConfig()
    chunkedClient.uploadLimits = user.publicConfig?.upload_limits || defaultUploadLimits
    chunkedClient.importModulesMap = user.publicConfig?.import_module_map || {}
    importType.value = type
    await chunkedClient.checkQueue(
      importType.value.toLocaleLowerCase(),
      (resume: ChunkedUploadModelRecordData | null) => {
        if (resume && resume.id !== '0') {
          uploadResume.value = resume
        }
      },
    )
  }

  function removeUrlOrFile(index: number) {
    chunkedClient.cancelUpload()
    urls.value.splice(index, 1)
  }

  async function flushQueue() {
    chunkedClient.flushQueue(importType.value?.toLocaleLowerCase() || '')
    uploadResume.value = null
  }

  async function detectUpload(files: File[] | null) {
    if (!files?.[0]) {
      return
    }
    if (files.length > 1) {
      snack.add('You can drop only one file')
      return
    }

    await http.get(`/chunked_upload/flush_queue`, {}, true)
    if (files[0].type.includes('video/')) {
      dndFile.value = files[0]
      router.push('/creator/ebook/from-video')
      return
    }

    if (files[0].type.includes('audio/')) {
      dndFile.value = files[0]
      router.push('/creator/ebook/from-podcast')
      return
    }

    const typeMap: { [key: string]: string } = {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '/creator/ebook/from-docx',
      'application/pdf': '/creator/ebook/from-pdf',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': '/creator/ebook/from-pptx',
    }

    const newLocation = typeMap[files[0].type]
    if (newLocation) {
      dndFile.value = files[0]
      router.push(newLocation)
      return
    }
    snack.add('Unknown file type', { type: 'danger' })
  }

  return {
    urls,
    isValidUrl,
    navigateToUrlImport,
    dndFile,
    importType,
    init,
    flushQueue,
    addFiles,
    chunkedClient,
    uploadResume,
    removeUrlOrFile,
    detectUpload,
  }
})
